import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

export interface RowAction {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  hidden?: boolean;
  danger?: boolean;
  divider?: boolean;
  description?: string;
}

interface RowActionsMenuProps {
  actions: RowAction[];
  label?: string;
  align?: 'left' | 'right';
  size?: 'sm' | 'md';
}

/**
 * Dropdown kebab-style menu used in table rows and card headers.
 * Renders into document.body via portal so it escapes overflow/clip contexts.
 */
export function RowActionsMenu({
  actions,
  label = 'Row actions',
  align = 'right',
  size = 'sm',
}: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const visible = actions.filter((a) => !a.hidden);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 220;
    const gap = 6;
    const left =
      align === 'right'
        ? Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth)
        : rect.left;
    const top = rect.bottom + gap;
    setPos({ top, left: Math.max(8, left) });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (
        menuRef.current?.contains(tgt) ||
        triggerRef.current?.contains(tgt)
      )
        return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleScroll = () => setOpen(false);
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  if (visible.length === 0) return null;

  const handleItemClick = async (action: RowAction) => {
    if (action.disabled) return;
    setOpen(false);
    try {
      await action.onClick();
    } catch (err) {
      // Errors are expected to be surfaced by the caller via toast.
      // eslint-disable-next-line no-console
      console.error(`[RowActionsMenu] action "${action.id}" failed`, err);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`ui-row-actions-trigger ui-row-actions-trigger-${size}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <EllipsisVerticalIcon className="w-4 h-4" aria-hidden="true" />
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="ui-row-actions-menu"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {visible.map((action, i) => {
              const Icon = action.icon;
              return (
                <React.Fragment key={action.id}>
                  {action.divider && i !== 0 && <div className="ui-row-actions-divider" role="separator" />}
                  <button
                    type="button"
                    role="menuitem"
                    className={[
                      'ui-row-actions-item',
                      action.danger ? 'ui-row-actions-item-danger' : '',
                      action.disabled ? 'ui-row-actions-item-disabled' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={action.disabled}
                    onClick={() => handleItemClick(action)}
                  >
                    {Icon && <Icon className="w-4 h-4 ui-row-actions-icon" />}
                    <span className="ui-row-actions-label">
                      <span className="ui-row-actions-title">{action.label}</span>
                      {action.description && (
                        <span className="ui-row-actions-desc">{action.description}</span>
                      )}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

export default RowActionsMenu;
